import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export type BreadcrumbItem = {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
};

const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  ({ items, separator, className }, ref) => {
    const defaultSeparator = (
      <ChevronRight size={12} className="shrink-0 text-content-faint" aria-hidden />
    );
    const sep = separator ?? defaultSeparator;

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex items-center gap-1.5 text-xs', className)}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const content = (
            <>
              {item.icon && <span className="shrink-0">{item.icon}</span>}
              {item.label}
            </>
          );

          const linkClasses = cn(
            'flex items-center gap-1.5 transition-colors',
            isLast
              ? 'text-content font-medium'
              : 'text-content-tertiary hover:text-content-secondary cursor-pointer',
          );

          let element: React.ReactNode;
          if (item.onClick && !isLast) {
            element = (
              <button
                type="button"
                onClick={item.onClick}
                className={linkClasses}
              >
                {content}
              </button>
            );
          } else if (item.href && !isLast) {
            element = (
              <a href={item.href} className={linkClasses}>
                {content}
              </a>
            );
          } else {
            element = (
              <span className={linkClasses}>
                {content}
              </span>
            );
          }

          return (
            <React.Fragment key={index}>
              {index > 0 && sep}
              {element}
            </React.Fragment>
          );
        })}
      </nav>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
